// URL-based language routing.
//
// The site language lives in the URL path as a prefix: /en/..., /ru/..., /am/...
// Georgian is the default and carries no prefix (/car/519). React Router is given
// a matching `basename`, so every <Link> and route in the app automatically keeps
// the active prefix without per-link changes. localStorage.language mirrors the
// URL so the existing currentLanguage() reader keeps working everywhere.

// Languages that appear as a URL prefix (geo is the un-prefixed default).
export const PREFIX_LANGS = ["en", "ru", "am"];

// Country (ISO-3166 alpha-2) -> site language, for first-visit auto-detection.
const COUNTRY_LANG = {
    GE: "geo",
    AM: "am",
    RU: "ru", BY: "ru", KZ: "ru", KG: "ru", // Russian-speaking CIS
};

/** The language encoded in a path's prefix, or null when there is none. */
export function langFromPath(pathname) {
    const seg = (pathname || "").split("/")[1];
    return PREFIX_LANGS.includes(seg) ? seg : null;
}

/** The current URL's language ("geo" when un-prefixed). */
export function currentUrlLang() {
    return langFromPath(window.location.pathname) || "geo";
}

/** Router basename for the current URL ("/en", "/ru", "/am", or "/"). */
export function currentBasename() {
    const l = langFromPath(window.location.pathname);
    return l ? `/${l}` : "/";
}

/** A path with any language prefix removed (always starts with "/"). */
export function stripLangPrefix(pathname) {
    const stripped = (pathname || "/").replace(/^\/(en|ru|am)(?=\/|$)/, "");
    return stripped === "" ? "/" : stripped;
}

/** Build the URL for `code`, keeping the current (de-prefixed) path + query. */
export function buildLangUrl(code, pathname = window.location.pathname, search = window.location.search) {
    const base = stripLangPrefix(pathname);
    const prefix = code && code !== "geo" ? `/${code}` : "";
    const url = `${prefix}${base}${search || ""}`;
    return url || "/";
}

/** Map a detected country code to a site language (English for other countries). */
export function langForCountry(country) {
    const c = (country || "").toUpperCase();
    if (!c) return null;
    return COUNTRY_LANG[c] || "en";
}

/**
 * Keep localStorage.language in sync with the URL so currentLanguage() matches
 * what the visitor sees. Call once at bootstrap, before React renders.
 */
export function syncLanguageFromUrl() {
    const lang = currentUrlLang();
    try { localStorage.setItem("language", lang); } catch (e) { /* ignore */ }
    // A prefixed URL is an explicit choice (navigated, shared or switched), so
    // never auto-redirect away from it.
    if (lang !== "geo") {
        try { localStorage.setItem("langLocked", "1"); } catch (e) { /* ignore */ }
    }
    return lang;
}

/** Prefix an absolute app path with the current URL language (for hard redirects
 *  via window.location, which — unlike router navigation — ignore the basename). */
export function langUrl(path) {
    const lang = currentUrlLang();
    const clean = path && path.startsWith("/") ? path : `/${path || ""}`;
    return (lang && lang !== "geo" ? `/${lang}` : "") + clean;
}

/** User picked a language from the switcher: lock it and navigate to its URL. */
export function switchLanguage(code) {
    try {
        localStorage.setItem("language", code);
        localStorage.setItem("langLocked", "1");
    } catch (e) { /* ignore */ }
    window.location.href = buildLangUrl(code);
}
